"use client"

import { motion, useReducedMotion } from "motion/react"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import type { TrackPrompt } from "@/types"

import { buildFeed } from "./feed-model"
import { SuggestionFeedItem } from "./suggestion-feed-item"

interface SuggestionFeedProps {
  prompts: TrackPrompt[]
  onPromptSelect?: (promptId: number) => void
}

// The centre stage: one conversation, not a wall of slots. Two zones —
// the playlist taking shape (curator picks + accepted, in track order),
// then the live stream of fresh suggestions, newest first.
export function SuggestionFeed({
  prompts,
  onPromptSelect,
}: SuggestionFeedProps) {
  const { picks, fresh } = buildFeed(prompts)
  const total = picks.length + fresh.length

  return (
    <section aria-labelledby="feed-heading" className="flex flex-col gap-6">
      <h2 id="feed-heading" className="text-primary text-xl font-semibold">
        Community Suggestions
      </h2>

      {total === 0 ? (
        <EmptyState size="md" className="py-12">
          <EmptyState.Content>
            <p className="text-primary text-md text-center font-semibold">
              Nothing here yet
            </p>
            <EmptyState.Description>
              The first answer sets the tone.
            </EmptyState.Description>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button color="secondary" size="md" href="#track-prompts">
              Answer a card
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      ) : (
        <>
          {picks.length > 0 && (
            <FeedZone label="On the playlist">
              {picks.map((entry, index) => (
                <FeedRow key={entry.key} index={index}>
                  <SuggestionFeedItem
                    entry={entry}
                    onPromptSelect={onPromptSelect}
                  />
                </FeedRow>
              ))}
            </FeedZone>
          )}

          {fresh.length > 0 && (
            <FeedZone label="New suggestions">
              {fresh.map((entry, index) => (
                <FeedRow key={entry.key} index={index}>
                  <SuggestionFeedItem
                    entry={entry}
                    onPromptSelect={onPromptSelect}
                  />
                </FeedRow>
              ))}
            </FeedZone>
          )}
        </>
      )}
    </section>
  )
}

function FeedZone({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-tertiary text-xs font-medium tracking-wider uppercase">
        {label}
      </h3>
      {children}
    </div>
  )
}

// Entrance is a whisper: small fade/rise, stagger capped so a 40-item
// feed doesn't turn into a minute of choreography.
const MAX_STAGGER_MS = 300

function FeedRow({ index, children }: { index: number; children: ReactNode }) {
  const reduceMotion = useReducedMotion()

  if (reduceMotion) return <div>{children}</div>

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.25,
        ease: "easeOut",
        delay: Math.min(index * 50, MAX_STAGGER_MS) / 1000,
      }}
    >
      {children}
    </motion.div>
  )
}
